import type { HabitCompletion, LocalDateKey } from '@/types';
import {
  addDaysToDateKey,
  compareDateKeys,
  differenceInCalendarDays,
  enumerateDateKeys,
  startOfMonthKey
} from './dates';

export interface StreakCalculation {
  currentStreak: number;
  longestStreak: number;
  weeklyRate: number;
  monthlyRate: number;
  lastCompletedDate?: LocalDateKey;
}

function roundedRate(completed: number, scheduled: number): number {
  return scheduled === 0 ? 0 : Math.round((completed / scheduled) * 100);
}

function effectiveCompletions(
  completions: readonly HabitCompletion[]
): Map<LocalDateKey, HabitCompletion> {
  const byDate = new Map<LocalDateKey, HabitCompletion>();
  for (const completion of completions) {
    const existing = byDate.get(completion.date);
    if (!existing || completion.updatedAt >= existing.updatedAt)
      byDate.set(completion.date, completion);
  }
  return byDate;
}

export function calculateStreak(
  completions: readonly HabitCompletion[],
  referenceDate: LocalDateKey,
  scheduledDates?: readonly LocalDateKey[]
): StreakCalculation {
  const completionByDate = effectiveCompletions(
    completions.filter((completion) => compareDateKeys(completion.date, referenceDate) <= 0)
  );
  const knownDates = [...completionByDate.keys()].sort();
  const firstDate = knownDates[0] ?? referenceDate;
  const dates = scheduledDates
    ? [...new Set(scheduledDates)]
        .filter(
          (date) =>
            compareDateKeys(date, firstDate) >= 0 && compareDateKeys(date, referenceDate) <= 0
        )
        .sort()
    : enumerateDateKeys(firstDate, referenceDate);

  let longestStreak = 0;
  let running = 0;
  for (const date of dates) {
    const status = completionByDate.get(date)?.status;
    if (status === 'completed') {
      running += 1;
      longestStreak = Math.max(longestStreak, running);
    } else if (status !== 'excused') {
      running = 0;
    }
  }

  let currentStreak = 0;
  for (let index = dates.length - 1; index >= 0; index -= 1) {
    const date = dates[index];
    if (!date) continue;
    const status = completionByDate.get(date)?.status;
    if (status === 'completed') {
      currentStreak += 1;
      continue;
    }
    if (status === 'excused') continue;
    // An unfinished current day does not destroy yesterday's streak.
    if (date === referenceDate && status === undefined) continue;
    break;
  }

  const weeklyStart = addDaysToDateKey(referenceDate, -6);
  const monthlyStart = startOfMonthKey(referenceDate);
  const rateForRange = (start: LocalDateKey): number => {
    const inRange = dates.filter((date) => compareDateKeys(date, start) >= 0);
    const eligible = inRange.filter((date) => completionByDate.get(date)?.status !== 'excused');
    const completed = eligible.filter(
      (date) => completionByDate.get(date)?.status === 'completed'
    ).length;
    return roundedRate(completed, eligible.length);
  };
  const lastCompletedDate = [...completionByDate.values()]
    .filter((entry) => entry.status === 'completed')
    .map((entry) => entry.date)
    .sort()
    .at(-1);

  return {
    currentStreak,
    longestStreak,
    weeklyRate: rateForRange(weeklyStart),
    monthlyRate: rateForRange(monthlyStart),
    ...(lastCompletedDate ? { lastCompletedDate } : {})
  };
}

export function completionRate(
  completions: readonly HabitCompletion[],
  start: LocalDateKey,
  end: LocalDateKey
): number {
  if (differenceInCalendarDays(end, start) < 0) return 0;
  const byDate = effectiveCompletions(completions);
  const eligible = enumerateDateKeys(start, end).filter(
    (date) => byDate.get(date)?.status !== 'excused'
  );
  return roundedRate(
    eligible.filter((date) => byDate.get(date)?.status === 'completed').length,
    eligible.length
  );
}
